import m from 'mithril/hyperscript';
import { T, Translate } from '../../i18n';
import type { MithrilVNode } from '../../mithril';
import { KeyCode } from '../../services/util/key.binding';
import { classNames } from '../../util/classnames';
import { addStyles } from '../theme';
import { InputState } from './input.state';
import { CSS, Style } from './input.style';

export const MIN_NUMBER_ROWS = 1;

addStyles(Style);

function addStateClasses(state: InputState, classList: string[] = []): string[] {
	if (state.focused) {
		classList.push(CSS.isFocused);
		classList.push(CSS.MDL.isFocused);
	}

	if (state.isFilled) {
		classList.push(CSS.isDirty);
		classList.push(CSS.MDL.isDirty);
	}

	if (state.touched && !state.isValid) {
		classList.push(CSS.isInvalid);
		classList.push(CSS.MDL.isInvalid);
	}

	if (state.isLabelShown === false) {
		classList.push(CSS.isCompact);
	}

	return classList;
}

const FOCUS_NODE = (vnode: MithrilVNode) => {
	vnode?.dom.focus();
};

export interface InputCtx {
	rows?: number;
	type?: string;
	/**
	 * Returns only the raw input node its self.
	 * WARNING: This input node will not come with any of the visual validation states. It will _calculate them_ but the nodes that render them will not be included in the returned node
	 */
	rawInputOnly?: boolean;
}

export const Input = {
	CSS,
	view(state: InputState<any>, ctx: InputCtx = {}) {
		const classList = [
			CSS.container,
			CSS.MDL.container,
			`${CSS.container}--${state.id}`,
			`${CSS.container}--${state.label.toLowerCase()}`,
		];
		const className = addStateClasses(state, classList).join(' ');

		const onChange = (e) => Input.onChange(state, e);
		const onKeyUp = (e) => Input.onKeyUp(state, e);
		const onInput = (e) => Input.onInput(state, e);

		const attrs = {
			className: ctx.rawInputOnly ? '' : classNames(CSS.input, CSS.MDL.input),
			name: state.name,
			oninput: onInput,
			onchange: onChange,
			value: state.inputValue != null ? state.inputValue : '',
			title: state.hasError() ? Translate(state.error) : undefined,
			onfocus: (e) => Input.onFocus(state),
			onblur: (e) => Input.onBlur(state, e),
			oncreate: state.isAutoFocused ? FOCUS_NODE : state.focused ? FOCUS_NODE : undefined,
			onupdate: state.selected ? (vnode) => this.onSelected(vnode, state) : null,
			onkeyup: onKeyUp,
		} as any;

		// create textAreas when rows are defined
		const isTextArea = Boolean(ctx.rows);
		if (isTextArea) {
			attrs.rows = ctx.rows;
			attrs.style = { resize: 'none', overflow: 'hidden', outline: 'none' };
		} else {
			attrs.type = ctx.type ?? state.type;
		}

		if (state.disabled) {
			attrs.disabled = true;
		}

		if (state.readOnly) {
			attrs.readOnly = true;
		}

		if (state.placeholder != null) {
			attrs.placeholder = state.placeholder;
		}

		if (ctx.rawInputOnly) {
			return m(isTextArea ? 'textarea' : 'input', attrs);
		}

		let errorSpan: MithrilVNode | null = null;
		if (state.touched && state.isErrorShown) {
			if (state.hasError()) {
				errorSpan = m('span', { className: `${CSS.MDL.error} ${CSS.error}` }, Translate(state.error));
			} else if (state.isRequired && !state.isFilled) {
				errorSpan = m('span', { className: `${CSS.MDL.error} ${CSS.error}` }, Translate(T.INPUT_REQUIRED));
			}
		}

		const labelText = state.isLabelShown ? Translate(state.label) : '';
		const textfield = m('div', { className, 'data-state-id': state.id }, [
			m(isTextArea ? 'textarea' : 'input', attrs),
			m('label', { className: `${CSS.label} ${CSS.MDL.label}` }, labelText),
		]);
		return m('div', { className: CSS.wrapper }, [textfield, errorSpan]);
	},

	onSelected(vnode: MithrilVNode, state: InputState<string | number>) {
		(vnode?.dom as HTMLInputElement)?.select();
		state.setSelected(false);
	},

	onKeyUp(state: InputState<string | number>, e: Event) {
		const ev = e as KeyboardEvent;
		if (ev.keyCode === KeyCode.ENTER && ev.shiftKey && e.target instanceof HTMLInputElement && state.focused) {
			this.onInput(state, e, true);
		}
	},

	onChange(state: InputState<string | number>, e: Event) {
		const target = e.target as HTMLInputElement;
		state.setInputValue(target.value);
	},

	onInput(state: InputState<string | number>, e: Event, enter?: boolean) {
		state.onInput(e as InputEvent);
		const target = e.target as HTMLInputElement;
		if (enter) {
			state.setInputValue(target.value.concat('\n'));
		} else {
			state.setInputValue(target.value);
		}
		state.setTouched();
	},

	onFocus(state: InputState<string | number>) {
		state.setFocused(true);
	},

	onBlur(state: InputState<string | number>, e: FocusEvent) {
		state.onBlur(e);
		state.setFocused(false);
		state.setTouched();
	},
};
